import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChatTeardropDots, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Feedback() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "", q4: "", q5: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/feedback/config");
      setConfig(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (Object.values(answers).every((v) => !v.trim())) {
      toast.error("Please answer at least one question.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback/submit", answers);
      toast.success("Thanks - we've sent you an email with the quiz answers.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) return <div className="py-10 text-center text-[#7A828C]">Loading…</div>;

  if (!config.published) {
    return (
      <div data-testid="feedback-not-open" className="py-10 max-w-md mx-auto text-center">
        <ChatTeardropDots size={48} weight="duotone" className="text-[#000000] mx-auto mb-4" />
        <h1 className="font-display text-3xl mb-2">Feedback isn&apos;t open yet</h1>
        <p className="text-[#5a5a5a] mb-6 text-sm">The organisers will let you know when they&apos;re ready to hear your thoughts.</p>
        <Button onClick={() => navigate("/program")} variant="outline" className="rounded-sm" data-testid="feedback-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Back to programme
        </Button>
      </div>
    );
  }

  if (config.has_submitted) {
    return (
      <div data-testid="feedback-thanks" className="py-10 max-w-md mx-auto text-center">
        <CheckCircle size={56} weight="duotone" className="text-[#1f6b3a] mx-auto mb-4" />
        <h1 className="font-display text-3xl mb-2">Thanks!</h1>
        <p className="text-[#5a5a5a] mb-2 text-sm">Your feedback is in. We&apos;ve emailed you the quiz answers - check your inbox (and spam, just in case).</p>
        <p className="text-xs text-[#7A828C] mb-6 font-mono-time">Submitted {new Date(config.submitted_at).toLocaleString("en-GB")}</p>
        <Button onClick={() => navigate("/program")} variant="outline" className="rounded-sm" data-testid="feedback-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Back to programme
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="feedback-page" className="space-y-6 max-w-2xl">
      <div>
        <div className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest mb-1 flex items-center gap-1.5">
          <ChatTeardropDots size={12} weight="fill" /> Tell us
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">DFTB26 feedback</h1>
        <p className="text-sm text-[#5a5a5a] mt-2 leading-relaxed">
          Four quick questions. In return we&apos;ll email you the answers from the
          DFTB26 quiz. Free text - say as much or as little as you like.
        </p>
      </div>

      <div className="space-y-5">
        {(config.questions || []).map((q, i) => (
          <div key={q.key} data-testid={`feedback-q-${q.key}`}>
            <label htmlFor={`fb-${q.key}`} className="block">
              <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">Question {i + 1}</span>
              <span className="block font-display text-base mt-0.5 mb-2">{q.label}</span>
            </label>
            <Textarea
              id={`fb-${q.key}`}
              value={answers[q.key] || ""}
              onChange={(e) => setAnswers((cur) => ({ ...cur, [q.key]: e.target.value }))}
              rows={4}
              placeholder="Your thoughts…"
              className="rounded-sm"
              data-testid={`feedback-input-${q.key}`}
            />
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 flex justify-end pt-2">
        <Button
          onClick={submit}
          disabled={submitting}
          className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] h-12 px-6 font-semibold shadow-lg"
          data-testid="feedback-submit-btn"
        >
          {submitting ? "Sending…" : "Submit feedback"}
        </Button>
      </div>

      <p className="text-xs text-[#7A828C] text-center pb-4">
        You can only submit once. We&apos;ll email the quiz answers to <strong className="text-[#1c1c24]">{config.user_email || "your account email"}</strong>.
      </p>
    </div>
  );
}
